import { useEffect, useRef, useCallback, useState } from "react";

/**
 * useWebSocket — connects to the hub WebSocket, triggers
 * refreshData() on mutation events, and tracks live presence.
 *
 * Falls back to 60s polling if WS is unavailable.
 */
export default function useWebSocket(hubId, refreshData, identity) {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const pingTimer = useRef(null);
  const [onlineUsers, setOnlineUsers] = useState([]);

  // Stable refs to avoid reconnecting when callbacks/identity change
  const refreshRef = useRef(refreshData);
  refreshRef.current = refreshData;
  const identityRef = useRef(identity);
  identityRef.current = identity;

  const connect = useCallback(() => {
    if (!hubId) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
    const wsBase = backendUrl.replace(/^http/, "ws");
    const wsUrl = `${wsBase}/api/ws/${hubId}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        const id = identityRef.current;
        if (id?.userId) {
          ws.send(JSON.stringify({
            type: "identify",
            user_id: id.userId,
            role: id.role || "",
          }));
        }
        if (pingTimer.current) clearInterval(pingTimer.current);
        pingTimer.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 30000);
      };

      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (data.type === "pong" || data.type === "connected") return;
          if (data.type === "presence") {
            setOnlineUsers(data.payload?.online || []);
            return;
          }
          refreshRef.current();
        } catch {
          void 0;
        }
      };

      ws.onclose = () => {
        if (pingTimer.current) clearInterval(pingTimer.current);
        reconnectTimer.current = setTimeout(connect, 5000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      reconnectTimer.current = setTimeout(connect, 5000);
    }
  }, [hubId]);

  useEffect(() => {
    connect();

    const fallback = setInterval(() => {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        refreshRef.current();
      }
    }, 60000);

    return () => {
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.close();
      }
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (pingTimer.current) clearInterval(pingTimer.current);
      clearInterval(fallback);
    };
  }, [connect]);

  return { onlineUsers };
}
